import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { VERSAO_DO_PRODUTO } from '@/data/novidades';
import { AgendaGoogleDaConta } from './AgendaGoogleDaConta';
import { dados } from '@/data/porta';
import type { MinhaConta } from '@/data/tipos';
import { definirNovaSenha } from '@/session/sessao';

export interface EditarContaModalProps {
    open: boolean;
    onClose: () => void;
    onSalva: (conta: MinhaConta) => void;
}

/**
 * Nome e telefone de quem está na sessão.
 *
 * O item "Editar conta" fechava o menu e não fazia nada. O e-mail fica de fora
 * de propósito: é ele que amarra o login à clínica, e trocá-lo por aqui seria
 * outra forma de mudar de conta.
 */
export function EditarContaModal({ open, onClose, onSalva }: EditarContaModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idTelefone = useId();
    const [original, setOriginal] = useState<MinhaConta | null>(null);
    const [nome, setNome] = useState('');
    const [telefone, setTelefone] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        let vivo = true;
        setOriginal(null);
        setErro(null);
        dados().minhaConta()
            .then((c) => {
                if (!vivo) return;
                setOriginal(c);
                setNome(c.nome);
                setTelefone(c.telefone);
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler a sua conta.'); });
        return () => { vivo = false; };
    }, [open]);

    const sujo = original !== null && (nome !== original.nome || telefone !== original.telefone);

    const salvar = async (): Promise<boolean> => {
        if (!nome.trim()) {
            setErro('O nome não pode ficar vazio.');
            return false;
        }
        setSalvando(true);
        try {
            const salva = await dados().salvarMinhaConta({ nome: nome.trim(), telefone: telefone.trim() });
            aviso({ title: 'Conta atualizada', description: salva.nome });
            onSalva(salva);
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={440} title="Editar conta"
            description="Como o seu nome aparece para a equipe e nos registros."
            dirty={sujo} onSave={salvar}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!original || !sujo}>Salvar</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label="Nome" required htmlFor={idNome}>
                    <Input id={idNome} value={nome} onChange={(v) => { setNome(v); setErro(null); }} disabled={!original} />
                </FormField>
                <FormField label="Telefone" htmlFor={idTelefone}>
                    <Input id={idTelefone} type="tel" value={telefone} onChange={setTelefone} disabled={!original} />
                </FormField>
                {original && (
                    <FormField label="E-mail" hint="É ele que liga o login a esta clínica; não se troca por aqui">
                        <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{original.email}</span>
                    </FormField>
                )}
                <AgendaGoogleDaConta aberta={open} />
                {/* A versão do sistema, a mesma do rodapé do menu da conta: é o que se diz ao suporte. */}
                <p style={{ margin: 0, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                    Versão do sistema {VERSAO_DO_PRODUTO}
                </p>
            </div>
        </Modal>
    );
}

export interface SenhaModalProps {
    open: boolean;
    onClose: () => void;
}

/** Troca a senha da conta que está na sessão. O item do menu não fazia nada. */
export function SenhaModal({ open, onClose }: SenhaModalProps) {
    const aviso = useAviso();
    const idSenha = useId();
    const idRepetida = useId();
    const [senha, setSenha] = useState('');
    const [repetida, setRepetida] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSenha('');
        setRepetida('');
        setErro(null);
    }, [open]);

    const trocar = async (): Promise<boolean> => {
        /* A conferência é da tela: o servidor guardaria a senha que recebesse,
           inclusive a digitada errado. */
        if (senha.length < 8) { setErro('A senha precisa de pelo menos 8 caracteres.'); return false; }
        if (senha !== repetida) { setErro('As duas senhas não são iguais.'); return false; }
        setSalvando(true);
        try {
            const r = await definirNovaSenha(senha);
            if (!r.ok) {
                setErro(r.erro ?? 'Não foi possível trocar a senha.');
                return false;
            }
            aviso({ title: 'Senha trocada', description: 'Use a nova senha na próxima entrada.' });
            onClose();
            return true;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={440} title="Segurança e senha"
            description="A senha nova vale a partir da próxima entrada."
            dirty={senha !== '' || repetida !== ''} onSave={trocar} saveLabel="Trocar senha e fechar"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void trocar()} loading={salvando} disabled={!senha || !repetida}>Trocar senha</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label="Nova senha" required htmlFor={idSenha} hint="Pelo menos 8 caracteres">
                    <Input id={idSenha} type="password" autoComplete="new-password" value={senha} onChange={(v) => { setSenha(v); setErro(null); }} />
                </FormField>
                <FormField label="Repita a nova senha" required htmlFor={idRepetida}>
                    <Input id={idRepetida} type="password" autoComplete="new-password" value={repetida} onChange={(v) => { setRepetida(v); setErro(null); }} />
                </FormField>
            </div>
        </Modal>
    );
}
